import { PayloadAction } from "@reduxjs/toolkit";

import type {
  JobPriorityOption,
  JobsTypes,
  SingleJobsType,
  SortOption,
} from "@/redux/jobs/jobsType";

import { JobSingleType } from "@/commonTypes/jobsType";

type ChecklistModalPayload = {
  isOpen: boolean;
  data: JobSingleType | null;
  jobActiveTab?: string;
};
const defaultFiltersState = {
  searchString: "",
  jobPriority: { value: null, label: "Select.." } as JobPriorityOption,
  openStatus: "",
  openStatusValue: "",
  sortByTargetEndDate: { name: "", value: "" } as SortOption,
  sortByJobType: { name: "", value: "" } as SortOption,
  sortByStatus: { name: "", value: "" } as SortOption,
  sortByClientCode: { name: "", value: "" } as SortOption,
  sortByClientName: { name: "", value: "" } as SortOption,
  recently: { name: "", value: "" } as SortOption,
  offset: 0,
};

export const jobsReducer = {
  resetError: (state: JobsTypes) => {
    state.error = null;
    state.message = "";
  },
  resetSuccess: (state: JobsTypes) => {
    state.success = null;
  },
  resetSharedJobSuccess: (state: JobsTypes) => {
    state.sharedJobSuccess = null;
  },
  resetMessage: (state: JobsTypes) => {
    state.message = "";
  },
  resetStatusCode: (state: JobsTypes) => {
    state.statusCode = null;
  },
  resetFetching: (state: JobsTypes) => {
    state.fetching = false;
  },
  resetJobsTypeList: (state: JobsTypes) => {
    state.jobsTypeList = [];
  },
  resetNonRecurringJobsList: (state: JobsTypes) => {
    state.NonRecurringJobsList = [];
  },
  resetSingleJobsType: (state: JobsTypes) => {
    state.singleJobsType = {} as SingleJobsType;
  },
  resetRecurringJobsDetailsList: (state: JobsTypes) => {
    state.singleRecurringJobsDetails = {};
  },
  resetTotalCount: (state: JobsTypes) => {
    state.jobsTypeListTotalCount = 0;
  },
  resetJobsTotalCount: (state: JobsTypes) => {
    state.totalJobsCount = 0;
  },
  resetTotalRecurringJobsListCount: (state: JobsTypes) => {
    state.totalRecurringJobsListCount = 0;
  },
  resetJobsList: (state: JobsTypes) => {
    state.jobsList = [];
  },
  resetFutureJobsList: (state: JobsTypes) => {
    state.futureJobsList = [];
  },
  resetSingleJobs: (state: JobsTypes) => {
    state.singleJobs = {};
  },
  resetRecurringResponsiblePeople: (state: JobsTypes) => {
    state.recurringResponsiblePeople = [];
  },
  resetJobProgressCommentsSuccess: (state: JobsTypes) => {
    state.jobProgressCommentsSuccess = null;
  },
  resetJobProgressCommentsListing: (state: JobsTypes) => {
    state.jobProgressCommentsList = [];
  },
  resetDocumentsLinkList: (state: JobsTypes) => {
    state.documentsLinkList = [];
  },
  resetAssignedClientsOpenJobsList: (state: JobsTypes) => {
    state.assignedClientsOpenJobsList = [];
  },
  resetAssignedClientsShareOpenJobsList: (state: JobsTypes) => {
    state.assignedClientsShareOpenJobsList = [];
  },
  resetUniqueCheckMessage: (state: JobsTypes) => {
    state.uniqueCheckMessage = "";
  },
  resetUniqiueCheckFetching: (state: JobsTypes) => {
    state.uniqueCheckFetching = false;
  },
  resetUniqueCheckSuccess: (state: JobsTypes) => {
    state.uniqueCheckSuccess = null;
  },
  resetUniqueCheckStatusCode: (state: JobsTypes) => {
    state.uniqueCheckStatusCode = null;
  },
  resetJobChecklistData: (state: JobsTypes) => {
    state.jobChecklistData = [];
  },
  resetJobChecklistSingleData: (state: JobsTypes) => {
    state.jobChecklistSingleData = null;
  },
  resetJobCheckTotalCount: (state: JobsTypes) => {
    state.jobChecklistCount = 0;
  },
  resetAllOpenJobChecklistData: (state: JobsTypes) => {
    state.allOpenJobChecklistData = [];
  },
  resetAllOpenJobChecklistSingleData: (state: JobsTypes) => {
    state.allOpenJobChecklistSingleData = null;
  },
  resetAllOpenJobCheckTotalCount: (state: JobsTypes) => {
    state.allOpenJobChecklistCount = 0;
  },
  resetAllCommentDueTomorrowListData: (state: JobsTypes) => {
    state.allCommentDueTomorrowListData = [];
  },
  resetAllCommentDueTomorrowCount: (state: JobsTypes) => {
    state.allCommentDueTomorrowCount = 0;
  },
  resetRecurringJobReportList: (state: JobsTypes) => {
    state.recurringJobReportList = [];
  },
  resetRecurringJobReportTotalCount: (state: JobsTypes) => {
    state.recurringJobReportTotalCount = 0;
  },

  // ================= ALL OPEN JOBS FILTERS =================

  setSearchStringInAllOpenJobs: (
    state: JobsTypes,
    action: PayloadAction<string>
  ) => {
    state.filters.searchString = action.payload;
  },

  setJobPriorityInAllOpenJobs: (
    state: JobsTypes,
    action: PayloadAction<JobPriorityOption>
  ) => {
    state.filters.jobPriority = action.payload;
  },

  setOpenStatusInAllOpenJobs: (
    state: JobsTypes,
    action: PayloadAction<string>
  ) => {
    state.filters.openStatus = action.payload;
  },

  setOpenStatusValueInAllOpenJobs: (
    state: JobsTypes,
    action: PayloadAction<string>
  ) => {
    state.filters.openStatusValue = action.payload;
  },

  setSortByTargetEndDateInAllOpenJobs: (
    state: JobsTypes,
    action: PayloadAction<SortOption>
  ) => {
    state.filters.sortByTargetEndDate = action.payload;
  },

  setSortByJobTypeInAllOpenJobs: (
    state: JobsTypes,
    action: PayloadAction<SortOption>
  ) => {
    state.filters.sortByJobType = action.payload;
  },

  setSortByStatusInAllOpenJobs: (
    state: JobsTypes,
    action: PayloadAction<SortOption>
  ) => {
    state.filters.sortByStatus = action.payload;
  },

  setSortByClientCodeInAllOpenJobs: (
    state: JobsTypes,
    action: PayloadAction<SortOption>
  ) => {
    state.filters.sortByClientCode = action.payload;
  },

  setSortByClientNameInAllOpenJobs: (
    state: JobsTypes,
    action: PayloadAction<SortOption>
  ) => {
    state.filters.sortByClientName = action.payload;
  },

  setRecentlyInAllOpenJobs: (
    state: JobsTypes,
    action: PayloadAction<SortOption>
  ) => {
    state.filters.recently = action.payload;
  },

  setOffsetInAllOpenJobs: (state: JobsTypes, action: PayloadAction<number>) => {
    state.filters.offset = action.payload;
  },

  setChecklistModalState: (
    state: JobsTypes,
    action: PayloadAction<ChecklistModalPayload>
  ) => {
    state.checklistModalOpen = action.payload.isOpen;
    state.checklistJobData = action.payload.data;
    state.jobActiveTab = action.payload.jobActiveTab;
  },

  resetChecklistModalState: (state: JobsTypes) => {
    state.checklistModalOpen = false;
    state.checklistJobData = null;
    state.jobActiveTab = "allOpenJobs"; // default tab
  },
  resetAllOpenJobsFilters: (state: JobsTypes) => {
    state.filters = { ...defaultFiltersState };
  },
};
